import { useEffect, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';

import { focusMainContent } from '@shared/ui/skip-link';

// How long to wait for the new route to set its <title> (react-helmet-async
// writes it after the route renders, which can be after a lazy chunk loads).
const TITLE_POLL_MS = 100;
const TITLE_POLL_LIMIT = 10;

/**
 * Makes client-side navigation perceivable without sight: a full page load
 * is announced by the screen reader and resets focus to the top, but a SPA
 * route change does neither on its own. After every navigation except the
 * first load, this:
 *
 * - announces the new document title in a polite live region, and
 * - moves focus to the page's main content (`MAIN_CONTENT_ID`, the same
 *   target as the skip link), so the next Tab starts from the new content,
 *   not from wherever the old page left it.
 *
 * Focus only moves when the *route* changes, not when a param within it
 * does: swiping between articles in the reader rewrites the URL on every
 * swipe, and pulling focus each time would fight the reader's own controls.
 * It also stays put when it's already inside the main content.
 */
export function RouteAnnouncer() {
	const router = useRouter();
	const [message, setMessage] = useState('');
	const previousRouteId = useRef<string | null>(null);

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout> | undefined;

		const unsubscribe = router.subscribe('onResolved', (event) => {
			const routeId = router.state.matches.at(-1)?.routeId ?? null;
			const previous = previousRouteId.current;
			previousRouteId.current = routeId;

			// The first load is announced by the browser itself.
			if (previous === null || !event.pathChanged) {
				return;
			}

			const routeChanged = routeId !== previous;
			const titleBefore = document.title;
			let polls = 0;

			clearTimeout(timer);
			const settle = () => {
				polls += 1;
				if (document.title === titleBefore && polls < TITLE_POLL_LIMIT) {
					timer = setTimeout(settle, TITLE_POLL_MS);
					return;
				}
				setMessage(document.title);
				if (routeChanged) {
					focusMainContent();
				}
			};
			timer = setTimeout(settle, TITLE_POLL_MS);
		});

		return () => {
			clearTimeout(timer);
			unsubscribe();
		};
	}, [router]);

	return (
		<div className="sr-only" aria-live="polite" aria-atomic="true">
			{message}
		</div>
	);
}
